import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import Debug from 'debug';
const debug = Debug('model:calendar');
dayjs.extend(dayOfYear);

const Phases = {
  Morning: 0,
  Midday: 1,
  Evening: 2,
  Night: 3
};

class Calendar {
  static Spring = 'Spring';
  static Summer = 'Summer';
  static Autum = 'Autumn';
  static Winter = 'Winter';

  static NONE = 'NONE';
  static PARTIAL = 'PARTIAL';
  static DATE = 'DATE';
  static FULL = 'FULL';

  constructor(step = 0) {
    this.year = 1842;
    this.step = step;
    this.seasonOffsets = this.initSeasons(this.year); // TODO: Use this
    this.dateOffset = 181; // Start during summer
    this.date = dayjs(`${this.year}-01-01`).dayOfYear(this.dayOfYear());
    this.endDate = null;

    // Empty rather than undefined: a Calendar can be advanced before GameContext wires up
    // setCallbacks, and nextDay must not throw when it is.
    this.listeners = [];

    this.phases = [
      { index: Phases.Morning, isActive: false },
      { index: Phases.Midday, isActive: true },
      { index: Phases.Evening, isActive: false },
      { index: Phases.Night, isActive: false }
    ];
  }

  setCallbacks(callbacks) {
    this.listeners = callbacks || [];
  }

  dayOfYear() {
    return this.dateOffset + this.step;
  }

  setEndDate(offset) {
    this.endDate = dayjs(`${this.year}-01-01`).dayOfYear(this.dayOfYear() + offset);
  }

  getEndDate() {
    return this.endDate.format('MMM D, YYYY');
  }

  night() {
    this.phases = this.phases.map(phase => ({
      ...phase,
      isActive: phase.index === Phases.Night
    }));
  }

  isNight() {
    return this.phases[Phases.Night].isActive;
  }

  day() {
    this.phases = this.phases.map(phase => ({
      ...phase,
      isActive: phase.index === Phases.Midday
    }));
  }

  phaseOfDay() {
    return this.phases.findIndex(phase => phase.isActive);
  }

  next(context) {
    const index = this.phaseOfDay();
    this.phases[index].isActive = false;

    const nextIndex = (index + 1) % this.phases.length;
    this.phases[nextIndex].isActive = true;

    debug(index, nextIndex, this.phases.length);
    if (nextIndex === 0) this.nextDay(context);
  }

  nextStep(context) {
    if (this.isNight()) {
      this.day();
      this.nextDay(context);
    } else {
      this.night();
    }
  }

  // The clock's phase advance: flips day/night and nothing else. Deliberately does not
  // increment the day, so no listeners fire and no context is needed. The walk phase renders
  // Calendar.PARTIAL (icon only, no date), so the suppressed day count is invisible there.
  // See docs/tick-loop.md for what it takes to let the clock own days.
  advancePhase() {
    if (this.isNight()) this.day();
    else this.night();
  }

  delta(gain) {
    const { time } = gain;
    if (!time) return;

    for (let i = 0; i < time; i++) {
      this.nextDay();
    }
  }

  // Dispatch is synchronous, and one bad listener can never stop time. The old
  // setTimeout(…, 0) deferred a macrotask with a stale captured context and swallowed every
  // throw — which is how TOM_EARLY's TypeError silently aborted the rest of the chain, and
  // why daily upkeep never ran. The clock calls this every night->day, so it has to hold.
  nextDay(context) {
    this.step++;
    // TODO: Handle new year
    this.date = dayjs(`${this.year}-01-01`).dayOfYear(this.dayOfYear());

    this.listeners.forEach(callback => {
      try {
        callback(this.step, context);
      } catch (error) {
        debug('day-change listener failed', error);
      }
    });
  }

  format(setting) {
    if (setting === Calendar.FULL) {
      return {
        date: this.date.format('MMM DD, YYYY'),
        icon: this.seasonCode(this.season()),
        daysLeft: this.endDate?.diff(this.date, 'days')
      };
    }

    if (setting === Calendar.DATE) {
      return {
        date: this.date.format('MMM DD, YYYY'),
        icon: this.seasonCode(this.season())
      };
    }

    if (setting === Calendar.PARTIAL) return { icon: this.dayCode() };

    return [];
  }

  dayCode() {
    if (this.isNight()) {
      return '🌙';
    }
    return '☀'; //'☼';
  }

  seasonCode(season) {
    if (this.isNight()) {
      return '🌙';
    }
    switch (season) {
      case Calendar.Spring:
        return '🌱';
      case Calendar.Summer:
        return '☀'; //'☼';
      case Calendar.Autum:
        return '🍁';
      case Calendar.Winter:
        return '❄';
      default:
    }
  }

  initSeasons() {
    return {
      [Calendar.Spring]: 100,
      [Calendar.Summer]: 180,
      [Calendar.Autum]: 260,
      [Calendar.Winter]: 330
    };
  }
  season() {
    const dayOfYear = this.dayOfYear() % 365;
    if (dayOfYear <= this.seasonOffsets[Calendar.Spring]) {
      return Calendar.Winter;
    }

    // Spring
    // ✿
    if (dayOfYear <= this.seasonOffsets[Calendar.Summer]) {
      return Calendar.Spring;
    }

    // Summer
    // ☼
    if (dayOfYear <= this.seasonOffsets[Calendar.Autum]) return Calendar.Summer;

    // Fall
    // ☁
    if (dayOfYear <= this.seasonOffsets[Calendar.Winter]) return Calendar.Autum;

    // Winter
    // ❄
    return Calendar.Winter;
  }
}

export default Calendar;
